'use client';
import React from 'react';
import Image from 'next/image';
import { CardGridItem, CardLocale, getCardImage, getCardTitle } from '@/src/types/card';
import { getCardLogoAltSuffix } from '@/constants';
import fullLogoBlack from '@/src/assets/images/full-logo-black.svg';
import styles from './CardImage.module.css';

export type CardImageSize =
  | 'grid'              // ProductCardGrid listing
  | 'pdp'               // Product detail page hero
  | 'cart'              // Cart row thumb
  | 'thumb'             // AddedToCartModal
  | 'order'             // OrderDetail line item
  | 'searchResultSmall'; // SearchBar / HeroSearch dropdown

interface PresetConfig {
  width: number;
  height: number;
  sizes?: string;
}

// All rectangular presets use 5/4 aspect (matches the listing). thumb and
// searchResultSmall stay square because they're icons.
const PRESETS: Record<CardImageSize, PresetConfig> = {
  grid:              { width: 280, height: 224,  },
  pdp:               { width: 400, height: 320,  },
  cart:              { width: 240, height: 192,  },
  order:             { width: 120, height: 96,   },
  thumb:             { width: 60,  height: 50,  },
  searchResultSmall: { width: 60,  height: 50 },
};

interface Props {
  card: CardGridItem | null | undefined;
  locale: CardLocale;
  size: CardImageSize;
  eager?: boolean;
  /** Emit a <head> preload (next/image `priority`) for this image. Defaults
   * to following `eager`; pass false for eager images below the fold so they
   * don't compete with the page's LCP element for bandwidth. */
  preload?: boolean;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  /** Rendered as siblings of the IMG inside the paper wrapper - use for
   * discount ribbons, wishlist hearts, country flags, etc. The wrapper
   * already has position: relative, so children can position: absolute. */
  children?: React.ReactNode;
}

export const CardImage: React.FC<Props> = ({
  card,
  locale,
  size,
  eager,
  preload,
  className,
  onClick,
  children,
}) => {
  const src = getCardImage(card) || fullLogoBlack.src;
  const alt = `${card ? getCardTitle(card, locale) : ''}${getCardLogoAltSuffix()}`;
  const preset = PRESETS[size];
  const withPreload = preload ?? !!eager;
  // React 19 SSR auto-preloads any non-lazy <img> unless fetchPriority is
  // "low" - so an eager image with preload=false must be demoted to "low" or
  // it still lands in <head> and competes with the page's LCP element.
  let fetchPriority: 'high' | 'low' | undefined;
  if (size === 'pdp' && eager) fetchPriority = 'high';
  else if (eager && !withPreload) fetchPriority = 'low';

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    (e.currentTarget as HTMLImageElement).src = fullLogoBlack.src;
  };

  // The preset's width/height ARE the wrapper's dimensions. Applied inline
  // so callers don't need CSS for sizing. maxWidth/maxHeight 100% cap to
  // parent so the card never overflows a narrow viewport (e.g. PDP 400px
  // wrapper on a 320px phone). Skipped entirely when the caller passes a
  // className - that's the signal they're providing their own positioning
  // (e.g. listing's .cardImageFloat at 85% × 70% of its tile wrapper).
  const paperStyle: React.CSSProperties | undefined = className
    ? (onClick ? { cursor: 'pointer' } : undefined)
    : {
        width: preset.width,
        height: preset.height,
        maxWidth: '100%',
        maxHeight: '100%',
        ...(onClick && { cursor: 'pointer' }),
      };

  return (
    <div
      className={`${styles.paper}${className ? ` ${className}` : ''}`}
      style={paperStyle}
      onClick={onClick}
    >
      <Image
        src={src}
        alt={alt}
        width={preset.width}
        height={preset.height}
        unoptimized
        loading={eager ? 'eager' : 'lazy'}
        priority={withPreload}
        fetchPriority={fetchPriority}
        className={styles.img}
        onError={handleError}
      />
      {children}
    </div>
  );
};
